import { ReactNode } from 'react';
import { TSelfEnrollmentStatus, TSessionDetails } from '@api/io/session.io';
import { ContinueButton } from '@components/EnrollmentActions/ContinueButton';
import { DisenrollButton } from '@routes/session-details/components/Enrollment/Actions/DisenrollButton';
import { EnrollButton } from '@routes/session-details/components/Enrollment/Actions/EnrollButton';
import { useConfigContext } from '@fe-core';

type TEnrollmentAction = {
  enrollmentAction: ReactNode;
  disenrollmentAction?: ReactNode;
};

export const enrollmentActionsMap: Record<
  TSelfEnrollmentStatus,
  ({
    sessionDetails,
    baseUrl,
  }: {
    sessionDetails: TSessionDetails;
    baseUrl: string;
  }) => TEnrollmentAction
> = {
  unenrolled: ({ sessionDetails }) => ({
    enrollmentAction: sessionDetails.enrollment.enabled && (
      <EnrollButton sessionDetails={sessionDetails} />
    ),
    disenrollmentAction: null,
  }),
  active: ({ sessionDetails, baseUrl }) => ({
    enrollmentAction: (
      <ContinueButton
        link={`${baseUrl}session/${sessionDetails.id}`}
        progress={sessionDetails.selfEnrollmentProgress}
      />
    ),
    disenrollmentAction: (
      <DisenrollButton
        sessionId={sessionDetails.id}
        disabled={!sessionDetails.disenrollment.enabled}
      />
    ),
  }),
  enrolled_confirmed: ({ sessionDetails }) => ({
    enrollmentAction: null,
    disenrollmentAction: sessionDetails.enrollment.cancellable && (
      <DisenrollButton sessionId={sessionDetails.id} disabled={false} />
    ),
  }),
  unknown: () => ({
    enrollmentAction: null,
    disenrollmentAction: null,
  }),
};

type Props = {
  sessionDetails: TSessionDetails;
};

export const useEnrollmentActions = ({ sessionDetails }: Props) => {
  const { config } = useConfigContext();
  if (!sessionDetails.selfEnrollmentStatus) {
    return null;
  }

  return enrollmentActionsMap[sessionDetails.selfEnrollmentStatus]({
    sessionDetails,
    baseUrl: config.baseUrls.aleweb as string,
  });
};
